import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link as LinkIcon, Link2, Store, Plus, Loader2, RefreshCw, Ban, Check, Pencil, Phone, Copy, Building2, Settings } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';

interface Merchant {
  id: number;
  brandId: number | null;
  name: string;
  contactName?: string | null;
  phone?: string | null;
  email?: string | null;
  status: 'active' | 'inactive';
  notes?: string | null;
  address?: string | null;
  verifyCode?: string | null;
  brandName?: string | null;
  brandVerifyCode?: string | null;
  brandStatus?: 'active' | 'inactive' | null;
  activeOfferCount?: number;
  offerCount: number;
  pendingSubmissions: number;
  hasStandingLink?: boolean;
  promo?: { shares: number; impressions: number; claims: number };
  createdAt: number;
}

interface BrandGroup {
  key: string;
  brandId: number | null;
  brandName: string;
  brandVerifyCode: string | null;
  brandStatus: 'active' | 'inactive' | null;
  merchants: Merchant[];
  activeOfferTotal: number;
}

interface OfferOption {
  id: number;
  key: string;
  restaurantName: string;
  title: string;
  merchantId?: number | null;
  maxClaims?: number | null;
  validUntil?: number | null;
  isActive?: number | boolean;
  merchantVerifyCode?: string | null;
}

interface OfferEditForm {
  id: number;
  title: string;
  maxClaims: string;
  validUntil: string;
  verifyCode: string;
  isActive: boolean;
}

const emptyForm = { name: '', contactName: '', phone: '', email: '', address: '', verifyCode: '', notes: '' };

export default function AdminMerchants() {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [offers, setOffers] = useState<OfferOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Brand selection in create form（brandId 掛現有品牌；brandName 建新品牌）
  const [brandMode, setBrandMode] = useState<'existing' | 'new'>('existing');
  const [brandIdSel, setBrandIdSel] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [editingBrandName, setEditingBrandName] = useState('');

  // Brand settings modal（PUT /api/public/admin/brands/:id）
  const [brandTarget, setBrandTarget] = useState<BrandGroup | null>(null);
  const [brandForm, setBrandForm] = useState({ name: '', verifyCode: '', contactName: '', phone: '', status: 'active' as 'active' | 'inactive' });
  const [brandSaving, setBrandSaving] = useState(false);

  const [assignTarget, setAssignTarget] = useState<Merchant | null>(null);
  const [assignOfferId, setAssignOfferId] = useState('');
  const [linkResult, setLinkResult] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkNotified, setLinkNotified] = useState('');

  // Offers popup editor (click "N 個" to open)
  const [offersTarget, setOffersTarget] = useState<Merchant | null>(null);
  const [offerEdit, setOfferEdit] = useState<OfferEditForm | null>(null);
  const [offerSaving, setOfferSaving] = useState(false);
  const [offerError, setOfferError] = useState('');

  const merchantOffers = offersTarget
    ? offers.filter((o) => o.merchantId === offersTarget.id)
    : [];

  // 品牌 ▸ 分店 分組（brandId 為準；無 brandId 的舊資料歸入同名／自身 fallback 組）
  const brandGroups = useMemo<BrandGroup[]>(() => {
    const map = new Map<string, BrandGroup>();
    for (const m of merchants) {
      const key = m.brandId != null ? `id:${m.brandId}` : `name:${m.brandName || m.name}`;
      let g = map.get(key);
      if (!g) {
        g = {
          key,
          brandId: m.brandId ?? null,
          brandName: m.brandName || (m.brandId != null ? `品牌 #${m.brandId}` : m.name),
          brandVerifyCode: m.brandVerifyCode ?? null,
          brandStatus: m.brandStatus ?? null,
          merchants: [],
          activeOfferTotal: 0,
        };
        map.set(key, g);
      }
      g.merchants.push(m);
      g.activeOfferTotal += m.activeOfferCount || 0;
      if (!g.brandVerifyCode && m.brandVerifyCode) g.brandVerifyCode = m.brandVerifyCode;
      if (!g.brandStatus && m.brandStatus) g.brandStatus = m.brandStatus;
    }
    return [...map.values()];
  }, [merchants]);

  // 下拉用：只列有 brandId 的品牌
  const brandOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of merchants) {
      if (m.brandId != null && !map.has(m.brandId)) map.set(m.brandId, m.brandName || `品牌 #${m.brandId}`);
    }
    return [...map.entries()]
      .map(([id, name]) => ({ brandId: id, brandName: name }))
      .sort((a, b) => a.brandName.localeCompare(b.brandName, 'zh-HK'));
  }, [merchants]);

  const openOfferEdit = (o: OfferOption) => {
    setOfferError('');
    setOfferEdit({
      id: o.id,
      title: o.title || '',
      maxClaims: o.maxClaims ? String(o.maxClaims) : '',
      validUntil: o.validUntil ? new Date(o.validUntil * 1000).toISOString().slice(0, 10) : '',
      verifyCode: o.merchantVerifyCode || '',
      isActive: o.isActive === 1 || o.isActive === true,
    });
  };

  const reloadOffers = async () => {
    try {
      const res = await apiFetch('/api/public/admin/offers');
      const json = await res.json().catch(() => ({}));
      setOffers(Array.isArray(json) ? json : json.data || []);
    } catch { /* keep current list */ }
  };

  const saveOfferEdit = async () => {
    if (!offerEdit) return;
    setOfferSaving(true);
    setOfferError('');
    try {
      const body: Record<string, unknown> = {
        title: offerEdit.title.trim(),
        maxClaims: parseInt(offerEdit.maxClaims, 10) || 0,
        validUntil: offerEdit.validUntil ? Math.floor(new Date(offerEdit.validUntil).getTime() / 1000) : 0,
        merchantVerifyCode: offerEdit.verifyCode.trim(),
        isActive: offerEdit.isActive,
      };
      const res = await apiFetch(`/api/public/admin/offers/${offerEdit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data.data || data)?.error) throw new Error((data.data || data)?.error || '儲存失敗');
      setOfferEdit(null);
      await reloadOffers();
      load();
    } catch (err) {
      setOfferError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setOfferSaving(false);
    }
  };

  const issueLink = async (m: Merchant) => {
    if (m.hasStandingLink && !confirm('重新發放會即刻作廢舊連結（商家書籤會失效），新連結會自動 WhatsApp 發送給商家，確定？')) return;
    try {
      const res = await apiFetch(`/api/public/admin/merchants/${m.id}/issue-link`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      const d = data.data || data;
      if (!res.ok || !d?.url) throw new Error(d?.error || '發放失敗');
      setLinkResult(d.url);
      setLinkNotified(String(d.notifiedPhone || ''));
      setLinkCopied(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '發放失敗');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkResult);
    } catch {
      const el = document.createElement('textarea');
      el.value = linkResult;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, oRes] = await Promise.all([
        apiFetch('/api/public/admin/merchants'),
        apiFetch('/api/public/admin/offers'),
      ]);
      const mJson = await mRes.json().catch(() => ({}));
      const oJson = await oRes.json().catch(() => ({}));
      setMerchants(Array.isArray(mJson) ? mJson : mJson.data || []);
      setOffers(Array.isArray(oJson) ? oJson : oJson.data || []);
      setError('');
    } catch {
      setError('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = (group?: BrandGroup) => {
    setEditingId(null);
    setForm(emptyForm);
    if (group?.brandId != null) {
      setBrandMode('existing');
      setBrandIdSel(String(group.brandId));
    } else {
      setBrandMode('existing');
      setBrandIdSel('');
    }
    setNewBrandName('');
    setEditingBrandName('');
    setShowForm(true);
  };

  const openEdit = (m: Merchant) => {
    setEditingId(m.id);
    setForm({ name: m.name, contactName: m.contactName || '', phone: m.phone || '', email: m.email || '', address: m.address || '', verifyCode: m.verifyCode || '', notes: m.notes || '' });
    setEditingBrandName(m.brandName || (m.brandId != null ? `品牌 #${m.brandId}` : '（未綁定品牌）'));
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId && brandMode === 'existing' && !brandIdSel) { setError('請選擇品牌（或改選「新品牌」）'); return; }
    if (!editingId && brandMode === 'new' && !newBrandName.trim()) { setError('請填寫新品牌名稱'); return; }
    setSaving(true); setError('');
    try {
      const payload: Record<string, unknown> = { ...form };
      if (!editingId) {
        if (brandMode === 'existing') payload.brandId = parseInt(brandIdSel, 10);
        else payload.brandName = newBrandName.trim();
      }
      const res = await apiFetch(editingId ? `/api/public/admin/merchants/${editingId}` : '/api/public/admin/merchants', {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data.data || data)?.error) throw new Error((data.data || data)?.error || '儲存失敗');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const openBrandSettings = (g: BrandGroup) => {
    setBrandTarget(g);
    setBrandForm({
      name: g.brandName,
      verifyCode: g.brandVerifyCode || '1493',
      contactName: '',
      phone: '',
      status: g.brandStatus || 'active',
    });
  };

  const saveBrandSettings = async () => {
    if (!brandTarget || brandTarget.brandId == null) return;
    setBrandSaving(true); setError('');
    try {
      const body: Record<string, unknown> = {
        name: brandForm.name.trim(),
        verifyCode: brandForm.verifyCode.trim(),
        status: brandForm.status,
      };
      if (brandForm.contactName.trim()) body.contactName = brandForm.contactName.trim();
      if (brandForm.phone.trim()) body.phone = brandForm.phone.trim();
      const res = await apiFetch(`/api/public/admin/brands/${brandTarget.brandId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data.data || data)?.error) throw new Error((data.data || data)?.error || '儲存失敗');
      setBrandTarget(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setBrandSaving(false);
    }
  };

  const toggleStatus = async (m: Merchant) => {
    try {
      await apiFetch(`/api/public/admin/merchants/${m.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: m.status === 'active' ? 'inactive' : 'active' }),
      });
      load();
    } catch {
      setError('更新狀態失敗');
    }
  };

  const handleAssign = async () => {
    if (!assignTarget || !assignOfferId) return;
    try {
      await apiFetch(`/api/public/admin/merchants/${assignTarget.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offerId: parseInt(assignOfferId, 10) }),
      });
      setAssignTarget(null); setAssignOfferId('');
      load();
    } catch {
      setError('指派失敗');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin';
  };

  const inputCls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm';
  const brandBtnCls = 'inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700';

  return (
    <AdminLayout currentPage="merchants" onLogout={handleLogout}>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Store size={24} className="text-red-600" /> 商家中心管理
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            以品牌（例：華星冰室）分組管理分店；登記後商家可以在 <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">/merchant/center</code> 用 WhatsApp 驗證碼登入，睇自己優惠成效
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" title="重新整理">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => openCreate()} className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700">
            <Plus size={15} /> 新增分店
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300 flex items-center justify-between">
          {error}
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 text-xs">
            <tr>
              <th className="text-left px-4 py-3">品牌 ▸ 分店</th>
              <th className="text-left px-4 py-3">聯絡電話（登入用）</th>
              <th className="text-left px-4 py-3">優惠</th>
              <th className="text-left px-4 py-3">推廣成效</th>
              <th className="text-right px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading && (
              <tr><td colSpan={5} className="text-center py-12 text-gray-400"><Loader2 className="animate-spin inline mr-2" size={16} />載入中...</td></tr>
            )}
            {!loading && merchants.length === 0 && (
              <tr><td colSpan={5} className="text-center py-12 text-gray-400">未登記分店——新增品牌＋分店後，即可發 OTP 登入連結俾餐廳</td></tr>
            )}
            {!loading && brandGroups.map((g) => (
              <Fragment key={g.key}>
                {/* 品牌 header 行 */}
                <tr className="bg-gray-50 dark:bg-gray-900/60">
                  <td colSpan={5} className="px-4 py-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-900 dark:text-white">
                        <Building2 size={14} className="text-red-600 shrink-0" />
                        {g.brandName}
                        {g.brandStatus === 'inactive' && (
                          <span title="品牌已停用" className="inline-block w-2 h-2 rounded-full bg-gray-300 dark:bg-gray-600" />
                        )}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-300 font-bold"
                            title="品牌核銷碼（分店未自設碼時用此碼核銷）">
                        核銷碼 {g.brandVerifyCode || '1493'}
                      </span>
                      <span className="text-xs text-gray-400">{g.merchants.length} 間分店</span>
                      <span className="text-xs text-gray-400">· 進行中優惠 {g.activeOfferTotal}</span>
                      <span className="ml-auto flex items-center gap-2">
                        <button onClick={() => openCreate(g)} className={brandBtnCls}>
                          <Plus size={12} /> 新增分店
                        </button>
                        {g.brandId != null && (
                          <button onClick={() => openBrandSettings(g)} className={brandBtnCls}>
                            <Settings size={12} /> 品牌設定
                          </button>
                        )}
                      </span>
                    </div>
                  </td>
                </tr>
                {g.merchants.map((m) => (
                  <tr key={m.id} className="text-gray-700 dark:text-gray-300">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <span
                          title={m.status === 'active' ? '啟用' : '停用'}
                          className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                            m.status === 'active'
                              ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.9)]'
                              : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        />
                        <span className={`font-medium ${m.status === 'active' ? '' : 'text-gray-400 dark:text-gray-600'}`}>
                          {m.name}
                        </span>
                      </span>
                      {m.contactName && <p className="text-xs text-gray-400 ml-4">{m.contactName}</p>}
                      {m.address && <p className="text-xs text-gray-500 dark:text-gray-400 ml-4">📍 {m.address}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {m.phone ? (
                        <a href={`https://wa.me/${m.phone}`} target="_blank" rel="noopener noreferrer"
                           className={`inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline text-xs ${m.status === 'active' ? '' : 'opacity-50'}`}>
                          <Phone size={11} /> {m.phone}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <button
                        onClick={() => { setOffersTarget(m); setOfferEdit(null); setOfferError(''); }}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-bold"
                        title="查看及編輯此商家的優惠"
                      >
                        {m.offerCount} 個
                      </button>
                      {m.pendingSubmissions > 0 && <span className="text-amber-600 font-bold ml-1">（{m.pendingSubmissions} 待審）</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <span className="text-gray-400">📤 分享 {m.promo?.shares ?? 0}</span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="text-gray-400">👁 曝光 {m.promo?.impressions ?? 0}</span>
                      <span className="text-gray-400 mx-1.5">·</span>
                      <span className="text-green-600 font-bold">🎁 領取 {m.promo?.claims ?? 0}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => issueLink(m)} title={m.hasStandingLink ? '重新發放長期連結（舊連結即時失效）' : '發放長期登入連結'}
                                className={m.hasStandingLink ? 'text-green-500 hover:text-green-600' : 'text-gray-400 hover:text-blue-600'}>
                          <LinkIcon size={15} />
                        </button>
                        <button onClick={() => { setAssignTarget(m); setAssignOfferId(''); }} title="指派優惠"
                                className="text-gray-400 hover:text-blue-600"><Link2 size={15} /></button>
                        <button onClick={() => openEdit(m)} title="編輯"
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"><Pencil size={15} /></button>
                        <button onClick={() => toggleStatus(m)} title={m.status === 'active' ? '停用' : '啟用'}
                                className={`hover:text-red-600 ${m.status === 'active' ? 'text-gray-400' : 'text-green-500'}`}>
                          {m.status === 'active' ? <Ban size={15} /> : <Check size={15} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{editingId ? '編輯分店' : '新增分店'}</h2>
            <form onSubmit={handleSave} className="space-y-3">
              {editingId ? (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-1.5">品牌（不可更換）：</span>
                  <span className="font-bold inline-flex items-center gap-1">
                    <Building2 size={13} className="text-red-600" /> {editingBrandName || '（未綁定品牌）'}
                  </span>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
                  <p className="text-xs font-bold text-gray-500 dark:text-gray-400">品牌 *</p>
                  <div className="flex items-center gap-4 text-sm text-gray-700 dark:text-gray-300">
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={brandMode === 'existing'} onChange={() => setBrandMode('existing')} className="accent-red-600 w-4 h-4" />
                      現有品牌
                    </label>
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={brandMode === 'new'} onChange={() => setBrandMode('new')} className="accent-red-600 w-4 h-4" />
                      新品牌
                    </label>
                  </div>
                  {brandMode === 'existing' ? (
                    brandOptions.length > 0 ? (
                      <select value={brandIdSel} onChange={(e) => setBrandIdSel(e.target.value)} className={inputCls}>
                        <option value="">選擇品牌…</option>
                        {brandOptions.map((b) => (
                          <option key={b.brandId} value={b.brandId}>{b.brandName}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-amber-600 dark:text-amber-400">尚未有任何品牌——請改選「新品牌」填寫品牌名稱</p>
                    )
                  ) : (
                    <input value={newBrandName} onChange={(e) => setNewBrandName(e.target.value)}
                           placeholder="新品牌名稱 *（例：華星冰室；同名現有品牌會自動掛靠）" className={inputCls} />
                  )}
                </div>
              )}
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="分店名稱 *（例：華星冰室（尖沙咀））" className={inputCls} />
              <input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} placeholder="聯絡人" className={inputCls} />
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="聯絡電話 *（分店 UNIQUE，OTP 登入用，例如 8526xxxxxxx）" className={inputCls} />
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email（選填）" type="email" className={inputCls} />
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="門店地址（兌換門店清單用，分店＝一店一行）" className={inputCls} />
              <input value={form.verifyCode} onChange={(e) => setForm({ ...form, verifyCode: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="店員核銷驗證碼（4 位數字，留空＝用品牌碼）" inputMode="numeric" className={inputCls} />
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="備註" rows={2} className={inputCls + ' resize-none'} />
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg text-sm">取消</button>
                <button type="submit" disabled={saving || (!editingId && ((brandMode === 'existing' && !brandIdSel) || (brandMode === 'new' && !newBrandName.trim())))}
                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} 儲存
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Brand settings modal */}
      {brandTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setBrandTarget(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">品牌設定：{brandTarget.brandName}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">品牌核銷碼全線分店共用；個別分店可自設碼覆蓋</p>
            <div className="space-y-3">
              <input value={brandForm.name} onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} placeholder="品牌名稱 *" className={inputCls} />
              <input value={brandForm.verifyCode} onChange={(e) => setBrandForm({ ...brandForm, verifyCode: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="品牌核銷碼（4 位數字，預設 1493）" inputMode="numeric" className={inputCls} />
              <input value={brandForm.contactName} onChange={(e) => setBrandForm({ ...brandForm, contactName: e.target.value })} placeholder="品牌聯絡人（留空＝不變更）" className={inputCls} />
              <input value={brandForm.phone} onChange={(e) => setBrandForm({ ...brandForm, phone: e.target.value })} placeholder="品牌聯絡電話（留空＝不變更）" className={inputCls} />
              <select value={brandForm.status} onChange={(e) => setBrandForm({ ...brandForm, status: e.target.value as 'active' | 'inactive' })} className={inputCls}>
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
              </select>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setBrandTarget(null)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg text-sm">取消</button>
              <button onClick={saveBrandSettings} disabled={brandSaving || !brandForm.name.trim()}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1">
                {brandSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} 儲存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign offer modal */}
      {assignTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setAssignTarget(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">指派優惠給：{assignTarget.name}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">指派後商家會在商家中心看到該優惠的成效與驗證碼管理</p>
            <select value={assignOfferId} onChange={(e) => setAssignOfferId(e.target.value)} className={inputCls}>
              <option value="">選擇優惠…</option>
              {offers.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.restaurantName} — {o.title}{o.merchantId ? '（已指派）' : ''}
                </option>
              ))}
            </select>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setAssignTarget(null)} className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg text-sm">取消</button>
              <button onClick={handleAssign} disabled={!assignOfferId} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50">
                確認指派
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Merchant offers popup editor */}
      {offersTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOffersTarget(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{offersTarget.name} 的優惠</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">點擊「編輯」即場修改，無需切換到食肆優惠頁</p>

            {merchantOffers.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">尚未指派優惠——用操作欄的 🔗 指派一張優惠給此商家</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {merchantOffers.map((o) => {
                  const active = o.isActive === 1 || o.isActive === true;
                  return (
                    <li key={o.id} className="py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <span className="flex items-center gap-2">
                          <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${active ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.9)]' : 'bg-gray-300 dark:bg-gray-600'}`} />
                          <span className={`font-medium text-sm truncate ${active ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-600'}`}>{o.title}</span>
                        </span>
                        <p className="text-xs text-gray-400 mt-0.5">
                          名額 {o.maxClaims || '不限'}
                          {o.validUntil ? ` · 至 ${new Date(o.validUntil * 1000).toLocaleDateString('zh-HK')}` : ''}
                          {o.merchantVerifyCode ? ` · 核銷碼 ${o.merchantVerifyCode}` : ''}
                        </p>
                      </div>
                      <button onClick={() => openOfferEdit(o)}
                              className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600">
                        編輯
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {offerEdit && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white">編輯優惠</h3>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">優惠標題</label>
                  <input value={offerEdit.title} onChange={(e) => setOfferEdit({ ...offerEdit, title: e.target.value })}
                         className={inputCls} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">名額上限（留空＝不限）</label>
                    <input type="number" min="0" value={offerEdit.maxClaims} onChange={(e) => setOfferEdit({ ...offerEdit, maxClaims: e.target.value })}
                           className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">有效期至（留空＝長期）</label>
                    <input type="date" value={offerEdit.validUntil} onChange={(e) => setOfferEdit({ ...offerEdit, validUntil: e.target.value })}
                           className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">核銷碼（4 位；留空＝停用自助核銷）</label>
                    <input value={offerEdit.verifyCode} onChange={(e) => setOfferEdit({ ...offerEdit, verifyCode: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                           placeholder="1493" className={inputCls} />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer pb-2">
                    <input type="checkbox" checked={offerEdit.isActive} onChange={(e) => setOfferEdit({ ...offerEdit, isActive: e.target.checked })}
                           className="accent-red-600 w-4 h-4" />
                    上架中
                  </label>
                </div>
                {offerError && <p className="text-xs text-red-600">{offerError}</p>}
                <div className="flex gap-2 pt-1">
                  <button onClick={() => setOfferEdit(null)}
                          className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg text-sm">取消</button>
                  <button onClick={saveOfferEdit} disabled={offerSaving || !offerEdit.title.trim()}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1">
                    {offerSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} 儲存
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Standing link result modal */}
      {linkResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setLinkResult('')}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">商家長期連結已生成</h2>
            {linkNotified ? (
              <p className="text-xs text-green-600 dark:text-green-400 mb-3 flex items-center gap-1.5">
                ✅ 已自動 WhatsApp 發送至 {linkNotified}（商家可收藏該訊息作書籤）
              </p>
            ) : null}
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              商家撳連結即入商家中心（免驗證碼）。重新發放會作廢舊連結。以下複製按鈕僅作後備（例如想親自再發一次）。
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-700 dark:text-gray-300">
                {linkResult}
              </code>
              <button onClick={copyLink}
                      className={`px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1 ${linkCopied ? 'bg-green-600 text-white' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                <Copy size={13} /> {linkCopied ? '已複製' : '複製'}
              </button>
            </div>
            <button onClick={() => setLinkResult('')}
                    className="w-full mt-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg text-sm">
              關閉
            </button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
